import {
    ChangeDetectionStrategy, Component, OnDestroy, Inject, OnInit,
} from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { Observable, Subject, filter } from 'rxjs';
import { distinctUntilChanged, map, startWith, takeUntil } from 'rxjs/operators';

import { UpdateFilterObj } from '../../../shared/menu-categories/models/menu-categories.model';
import { CategoryFilterState } from '../models/category-page.models';
import { SetGridTypeCategoryGrid } from '../modules/category-grid/store/category-grid.actions';
import { gridDefaultFullyLoaded } from '../modules/category-grid/store/category-grid.selectors';
import { SetGridTypeCustomCategoryGrid } from '../modules/grid-custom-category/store/custom-category-grid.actions';
import { getCustomCategory } from '../modules/grid-custom-category/store/custom-category-grid.selectors';
import { FilterEmitter, ProductFilterItemArray, filterGroupArrayNames } from '../public_api';
import { CategoryPageService } from '../services/category-page.service';
import { ProductFilterService } from '../services/product-filter.service';
import { DestroyStateCategoryPage, SetSeoCategoryName } from '../store/category-page.actions';
import {
    getCategory,
    getFilters,
    getMenuCategory,
    getCmsLanding,
    getFiltersFilters,
    getProductsId,
    getFilterUsedAsSubcategories,
} from '../store/category-page.selectors';
import { ChangeGridType } from '../store/grid-type/grid-type.actions';
import { getGridType } from '../store/grid-type/grid-type.selectors';

import { Category } from '@core/domain/models/category.models';
import { getCmsConfigValue } from '@core/infrastructure/state/core.selectors';
import { SidebarCustomComponents, SidebarPublicModels, SidebarPublicPort } from '@features/sidebar';
import { MicrositeWindow } from '@pages/microsite/microsite.models';
import { CategoryNameInfo, ToolBarCategory } from '@shared/category-toolbar/models/category-tool-bar.model';
import { CategoryToolBarTransformer } from '@shared/category-toolbar/transformers/category-tool-bar.transformer';
import { MenuCategory } from '@shared/header/models/category.model';
import { GridType } from '@shared/models/grid-type.model';
import { ProductViewPlaceValue } from '@shared/product/models';
import { LazyLoadService } from '@shared/services/lazy-load.service';

@Component({
    selector: 'category-layout',
    templateUrl: './category-layout.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoryLayoutComponent implements OnInit, OnDestroy {
    public gridDefaultFullyLoaded$: Observable<boolean> = this.store.select(gridDefaultFullyLoaded);

    public hideRelateds$: Observable<boolean> = this.store.select(getCmsConfigValue('hide_related_products')).pipe(map((categories) => categories.includes(this.categoryId)));

    public readonly getCategory$: Observable<Category> = this.store.select(getCategory);

    public readonly getMenuCategory$: Observable<ToolBarCategory> = this.store.select(getMenuCategory).pipe(
        filter((category) => category.currentCategory && !!category.currentCategory.id && !!category.subCategories),
        map((category) => CategoryToolBarTransformer.TransformCategoryToToolbarCategory(category))
    );

    public readonly filtersProduct$: Observable<CategoryFilterState> = this.store.select(getFilters);

    public readonly isCustomCategory$: Observable<boolean> = this.getCategory$.pipe(
        map((category) => {
            if (!category?.id) return null;
            return !!CategoryPageService.getCustomCategory(category);
        })
    );

    public isFilterApplied = false;

    public showFilter = false;

    public hasMicrositeLegacy = false;

    private categoryId: string;

    private readonly gridCategoryTypeShown: 'standard' | 'custom' | undefined = undefined;

    private readonly onDestroy$ = new Subject<void>();

    public cmsLanding$: Observable<MenuCategory> = this.store.select(getCmsLanding).pipe(startWith(null));

    public totalFiltersCount$: Observable<number> = this.store.select(getFiltersFilters).pipe(
        // biome-ignore lint/complexity/noExtraBooleanCast: <explanation>
        map((filters) => !!filters ? ProductFilterService.getFilterApplied(filters).length : 0)
    );

    public gridViewValue$: Observable<number> = this.store.select(getGridType);

    public relatedProductsVm$ = this.getCategory$.pipe(
        distinctUntilChanged(),
        map((category) => ({
            place: ProductViewPlaceValue.PRODUCT_GRID,
            categoryId: category.id,
        }))
    );

    public productsId$$ = this.store.selectSignal(getProductsId);

    public customCategory$$ = this.store.selectSignal(getCustomCategory);

    public filters$$ = this.store.selectSignal(getFilters);

    public currentCategory$$ = this.store.selectSignal(getCategory);

    public filterTypeUsed$$ = this.store.selectSignal(getFilterUsedAsSubcategories);

    constructor(
        private readonly store: Store,
        private readonly lazyLoadService: LazyLoadService,
        private readonly productFilterService: ProductFilterService,
        @Inject(WINDOW) private readonly window: MicrositeWindow,
    ) {
        this.hasMicrositeLegacy = this.window?.inditex?.micrositeLegacy;
    }

    public ngOnInit(): void {
        this.filtersProduct$.pipe(takeUntil(this.onDestroy$)).subscribe((data) => {
            if (data?.filters) {
                this.isFilterApplied = data.isApplied;
            }
        });
    }

    public updateCategoryNameInfo(categoryNameInfo: CategoryNameInfo) {
        let seoCategoryName = '';
        if (categoryNameInfo.currentCategoryFromABTest?.seoCategory?.mainHeaderGaudi) {
            seoCategoryName = categoryNameInfo.currentCategoryFromABTest?.seoCategory?.mainHeaderGaudi;
        } else if (categoryNameInfo.currentCategoryFromABTest.seoCategory?.mainHeader) {
            seoCategoryName = categoryNameInfo.currentCategoryFromABTest?.seoCategory?.mainHeader;
        } else {
            seoCategoryName = categoryNameInfo.currentCategoryFromABTest?.seoCategory?.name;
        }
        this.store.dispatch(SetSeoCategoryName({ seoCategoryName }));
    }

    public ngOnDestroy(): void {
        this.store.dispatch(DestroyStateCategoryPage());
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    public setGridTypeCategoryGrid(gridType: GridType): void {
        this.store.dispatch(ChangeGridType({ gridType }));
        // Mantenemos lógica de Grids pero el gridType se gestiona en la store de category PAGE
        if (this.gridCategoryTypeShown === 'custom') this.store.dispatch(SetGridTypeCustomCategoryGrid({ initGridType: false }));
        else this.store.dispatch(SetGridTypeCategoryGrid({ initGridType: false }));
    }

    public updateFilter(updateFilterObj: UpdateFilterObj): void {
        const filterMapping = updateFilterObj.filterMapping;
        const filterTypeUsed = updateFilterObj.filterTypeUsed;

        const filterUpdate: FilterEmitter = {
            filtersGroup: filterTypeUsed as filterGroupArrayNames,
            filtersGroupArray: {
                [filterTypeUsed]: filterMapping,
            } as unknown as ProductFilterItemArray,
        };

        this.productFilterService.updateFilterFromOutside(filterUpdate);
    }

    public updateCategoryId(categoryId: string): void {
        this.categoryId = categoryId;
    }

    public updateShowFilter(showFilter: boolean): void {
        this.showFilter = showFilter;
    }

    public openFilterPanel() {
        SidebarPublicPort(this.store).openSidebarCustom({
            component: SidebarCustomComponents.FILTER,
            initializeFunction: undefined,
            size: SidebarPublicModels.size.XL,
            data: { },
        });
        if (typeof hj !== 'undefined') {
            hj('tagRecording', ['filtros']);
        }
    }
}
